import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {forkJoin} from 'rxjs';
import {ControlContainer, NgForm} from '@angular/forms';
import {StateService} from '../../../core/services/state.service';
import {ClassificatorValueQuery, LanguageQuery, LeModalService} from 'common-shared';
import {PropertyQuery} from 'common-shared';
import {UnitQuery} from 'common-shared';
import {LanguageService} from '../../languages/language.service';
import {PropertyService} from '../../../properties/property.service';
import {ClassificatorValuesQuery} from 'common-shared';
import {UnitService} from '../../units/unit.service';
import {PropertyLineModel} from '../../../properties/models/property-line.model';
import {PropertyMappingModel} from '../../../properties/models/property-mapping.model';
import {isNullOrWhiteSpace, numberCompare} from 'common-shared';
import {PropertyMappingService} from '../../../properties/property-mapping.service';
import {map} from 'rxjs/operators';

@Component({
  selector: 'mes-web-property-mapping-line-editor',
  templateUrl: './property-mapping-line-editor.component.html',
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class PropertyMappingLineEditorComponent implements OnInit {
  languages: LanguageQuery[] = [];
  classificatorValues: ClassificatorValueQuery[] = [];
  properties: PropertyQuery[] = [];
  mandatories: ClassificatorValueQuery[] = [];
  units: UnitQuery[] = [];
  showSource = false;
  localizationsEnabled: false;

  constructor(private languageService: LanguageService, public propertyService: PropertyService, public classificatorValueService: ClassificatorValuesQuery, private unitService: UnitService, private modalService: LeModalService, private stateService: StateService) {
  }

  @Input()
  propertyLines: PropertyLineModel[];
  @Input()
  propertyMappings: PropertyMappingModel[];
  @Input()
  sourceId: string;
  @Input()
  sourceCode: string;
  @Input()
  sourceType: string;
  @Input()
  activityId: string;
  @Input()
  orderOriginId: string;
  @Input()
  showLocalizations: boolean;

  @Output() removeLine = new EventEmitter<{ $mapping: PropertyMappingModel, $line: PropertyLineModel }>();
  @Output() lineChanged = new EventEmitter<{ $mapping: PropertyMappingModel, $line: PropertyLineModel }>();
  @Output() overwriteLine = new EventEmitter<{ $mapping: PropertyMappingModel, $line: PropertyLineModel }>();

  ngOnInit() {
    this.stateService.settings$.subscribe(value => {
      this.localizationsEnabled = value.Property.Localization;
    });

    forkJoin(
      this.languageService.getAll$.pipe(map(values => this.languages = values)),
      this.propertyService.getAll$.pipe(map(values => this.properties = values)),
      this.classificatorValueService.getAll$.pipe(map(values => this.classificatorValues = values)),
      this.classificatorValueService.mandatories$.pipe(map(values => this.mandatories = values)),
      this.unitService.query.selectAll().pipe(map(value => this.units = value))
    ).subscribe(value => {
      this.propertyMappings = this.propertyMappings || [];
    });
  }

  getSource(mapping) {
    return mapping.$inheritedFrom || mapping;
  }

  getLineSource(line) {
    return line.$inheritedFrom || line;
  }

  getLine(mapping) {
    let pl;
    if (mapping.$line) {
      pl = mapping.$line;
    } else {
      pl = this.propertyLines.find(value => value.PropertyId === mapping.PropertyId);
    }
    if (!pl) {
      pl = {
        PropertyId: mapping.PropertyId
      };
    }

    mapping.$line = pl;
    pl.PropertyId = mapping.PropertyId;
    return pl;
  }

  toggleSource() {
    this.showSource = !this.showSource;
  }

  getDefaultMappingLines() {
    return (this.propertyMappings || [])
      .filter(mapping => !mapping.LanguageId)
      .map((mapping: PropertyMappingModel) => {
        mapping.$Sequence = null;
        const prop = this.getProperty(mapping.PropertyId);
        if (prop) {
          mapping.$Sequence = prop.Sequence;
          mapping.$Code = prop.Code;
        }
        if (mapping.$Sequence === null) {
          mapping.$Sequence = 99999999998;
        }

        if (!!mapping.LanguageId) {
          mapping.$Language = (this.languages.find(value => value.Id === mapping.LanguageId) || {Code: mapping.LanguageId}).Code;
        } else {
          mapping.$Language = 'Z';
        }

        if (mapping.$isNew) {
          mapping.$Sequence = 99999999999;
        }

        return mapping;
      }).sort((a, b) => numberCompare(a, b, obj => obj.$Sequence || Number.MAX_VALUE))
      ;
    // .sort((a, b) =>
    //   stringCompare(a, b, obj => obj.$Language) || numberCompare(a, b, obj => obj.$Sequence) || stringCompare(a, b, obj => obj.$Code)
    // );
  }

  isLineVisible(mapping) {
    return true;
  }

  isLineFreezed(mapping) {
    if (!mapping) {
      return true;
    }
    if (mapping.SourceId === null) {
      return false;
    }
    return mapping.SourceId !== this.sourceId;

  }

  isFormulaFreezed(mapping) {
    if (!mapping) {
      return true;
    }
    if (mapping.SourceId !== this.sourceId) {
      return true;
    }
    const line = this.getLine(mapping);
    if (PropertyMappingService.lineHasValue(this.getProperty(mapping.PropertyId), line) && isNullOrWhiteSpace(mapping.Expression.Formula)) {
      return true;
    }
    return false;
  }

  isPropertyLineFreezed(mapping) {
    if (!mapping) {
      return true;
    }
    if (!isNullOrWhiteSpace(mapping.Expression.Formula)) {
      return true;
    }
    if (this.getLine(mapping).SourceId === this.sourceId) {
      return false;
    }
    // Freezed and Formula has value
    if (mapping.MandatoryClv === '2b72b0e8-8fb0-473c-b5b9-0b590dd6475f' && !isNullOrWhiteSpace(mapping.Expression.Formula)) {
      return true;
    }
    return true;
  }

  getMappingPerLanguage(mapping) {
    return PropertyMappingService.getMappingPerLanguage(this.propertyMappings, mapping.PropertyId, this.languages);
  }

  getMandatories(mapping) {
    let mandatories = this.mandatories;
    if (!isNullOrWhiteSpace(mapping.Expression.Formula)) {
      mandatories = this.mandatories.filter(clv => {
        return clv.Id === '2b72b0e8-8fb0-473c-b5b9-0b590dd6475f' || clv.Id === 'd27e7d24-7767-43a8-8d8a-dcf644ca752d';
      });
    }

    return mandatories;
  }

  onDefaultValueChanged(val, propertyLine, mapping) {
    this.lineChanged.emit({$mapping: mapping, $line: propertyLine});
  }

  overwrite(mapping, line) {
    this.overwriteLine.emit({$mapping: mapping, $line: line});
  }

  canDelete(mapping, line) {
    return mapping.SourceId === this.sourceId || line.SourceId === this.sourceId;
  }

  delete(mapping, line) {
    this.removeLine.emit({$mapping: mapping, $line: line});
  }

  onMappingChanged(val, mapping) {
    mapping.$line.PropertyId = mapping.PropertyId;
    this.lineChanged.emit({$mapping: mapping, $line: mapping.$line});
  }

  getProperty(propertyId) {
    return this.properties.find(p => p.Id === propertyId);
  }


}
